<template>
  <div class="explanation-viewer" :style="{ '--ec-offset': `${topOffset}px` }">
    <div
      v-if="lastUpdatedDisplay"
      class="d-flex align-center mb-4 text-caption text-grey-darken-1"
      style="gap: 8px"
    >
      <v-chip size="small" color="info" variant="tonal" class="px-3">
        <v-icon start size="16">mdi-clock-outline</v-icon>
        Updated
        {{ lastUpdatedDisplay.relative }}
      </v-chip>
      <span> ({{ lastUpdatedDisplay.absolute }}) </span>
    </div>

    <div class="explanation-viewer__reader">
      <div v-if="answerTitle" class="answer-title explanation-viewer__title">
        {{ answerTitle }}
      </div>

      <ExplanationContents
        v-if="shouldShowContents"
        :items="contentsItems"
        :active-id="activeId"
        :sticky="stickyContents"
      />

      <article class="explanation-viewer__article">
        <div
          v-if="visibleAnswerImages.length > 0 && answerImageLocation === 'top'"
          class="mb-6"
        >
          <v-row wrap>
            <v-col
              v-for="aImage in visibleAnswerImages"
              :key="aImage"
              :cols="answerImageColsValue"
            >
              <ImgExpander height="300" :src="allImgPaths[aImage]" />
              <div
                v-if="sanitizedAnswerImageCaptions[aImage]"
                class="text-caption font-italic mt-1 text-center px-4 v-html-content"
                v-html="sanitizedAnswerImageCaptions[aImage]"
              ></div>
            </v-col>
          </v-row>
        </div>

        <div
          v-if="explanationHtmlWithAnchors"
          ref="bodyRef"
          v-html="explanationHtmlWithAnchors"
          class="tiptap v-html-content explanation-viewer__body"
        ></div>

        <slot name="rating"></slot>

        <v-row wrap class="mt-4">
          <v-col
            v-if="visibleUniversalImages.length > 0"
            cols="12"
            class="mb-6"
          >
            <v-row wrap>
              <v-col
                v-for="aImage in visibleUniversalImages"
                :key="aImage"
                :cols="universalImagesColsValue"
              >
                <ImgExpander height="300" :src="allImgPaths[aImage]" />
                <div
                  v-if="sanitizedUniversalImageCaptions[aImage]"
                  class="text-caption font-italic mt-1 text-center px-4 v-html-content"
                  v-html="sanitizedUniversalImageCaptions[aImage]"
                ></div>
              </v-col>
            </v-row>
          </v-col>

          <v-col
            v-if="
              visibleAnswerImages.length > 0 && answerImageLocation === 'bottom'
            "
            cols="12"
            class="mb-6"
          >
            <v-row wrap>
              <v-col
                v-for="aImage in visibleAnswerImages"
                :key="aImage"
                :cols="answerImageColsValue"
              >
                <ImgExpander height="300" :src="allImgPaths[aImage]" />
                <div
                  v-if="sanitizedAnswerImageCaptions[aImage]"
                  class="text-caption font-italic mt-1 text-center px-4 v-html-content"
                  v-html="sanitizedAnswerImageCaptions[aImage]"
                ></div>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </article>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import ExplanationContents from "@/components/ExplanationContents.vue";
import ImgExpander from "@/components/ImgExpander.vue";
import { useExplanationContentsScroll } from "@/composables/explanationContentsScroll.js";
import {
  CONTENTS_MIN_HEADINGS,
  buildExplanationContents,
  createExplanationContentsIdPrefix,
} from "@/utils/explanationContents.mjs";
import {
  collectInlineExplanationImageUrls,
  filterDuplicateExplanationImages,
} from "@/utils/explanationImageDedupe.mjs";
import {
  sanitizeCaptionHtml,
  sanitizeExplanationHtml,
} from "@/utils/explanationHtmlSanitizer";

const props = defineProps({
  // Explanation content
  explanationHTML: {
    type: String,
    default: "",
  },
  answerTitle: {
    type: String,
    default: "",
  },

  // Universal/Explanation images (from the explanation entity)
  universalImages: {
    type: Array,
    default: () => [],
  },
  universalImagesCols: {
    type: Number,
    default: 1,
  },
  universalImageCaptions: {
    type: Object,
    default: () => ({}),
  },

  // Question-specific answer images (optional, for question bank only)
  answerImages: {
    type: Array,
    default: () => [],
  },
  answerImageCols: {
    type: Number,
    default: 1,
  },
  answerImageCaptions: {
    type: Object,
    default: () => ({}),
  },
  answerImageLocation: {
    type: String,
    default: "bottom",
    validator: (value) => ["top", "bottom"].includes(value),
  },

  // Image paths map (required)
  allImgPaths: {
    type: Object,
    required: true,
  },

  // Last updated display info
  lastUpdatedDisplay: {
    type: Object,
    default: null,
  },
  showContents: {
    type: Boolean,
    default: true,
  },
  stickyContents: {
    type: Boolean,
    default: true,
  },
  topOffset: {
    type: Number,
    default: 72,
  },
});

const sanitizeCaptionMap = (captions) => {
  if (!captions || typeof captions !== "object") return {};
  const sanitized = {};
  Object.entries(captions).forEach(([id, value]) => {
    if (typeof value !== "string") return;
    const cleaned = sanitizeCaptionHtml(value);
    if (cleaned) {
      sanitized[id] = cleaned;
    }
  });
  return sanitized;
};

const sanitizedExplanationHTML = computed(() =>
  sanitizeExplanationHtml(props.explanationHTML || ""),
);

const contentsIdPrefix = createExplanationContentsIdPrefix();

const explanationContents = computed(() =>
  buildExplanationContents(sanitizedExplanationHTML.value, {
    idPrefix: contentsIdPrefix,
  }),
);

const explanationHtmlWithAnchors = computed(
  () => explanationContents.value.html,
);

const inlineExplanationImageUrls = computed(() =>
  collectInlineExplanationImageUrls(explanationHtmlWithAnchors.value),
);

const visibleAnswerImages = computed(() =>
  filterDuplicateExplanationImages({
    imageIds: props.answerImages,
    allImgPaths: props.allImgPaths,
    excludeImageUrls: inlineExplanationImageUrls.value,
  }),
);

const visibleUniversalImages = computed(() =>
  filterDuplicateExplanationImages({
    imageIds: props.universalImages,
    allImgPaths: props.allImgPaths,
    excludeImageIds: visibleAnswerImages.value,
    excludeImageUrls: inlineExplanationImageUrls.value,
  }),
);

const contentsItems = computed(() => explanationContents.value.items);

const shouldShowContents = computed(
  () =>
    props.showContents && contentsItems.value.length >= CONTENTS_MIN_HEADINGS,
);

const bodyRef = ref(null);
const contentsItemIds = computed(() =>
  contentsItems.value.map((item) => item.id),
);

const { activeId } = useExplanationContentsScroll({
  bodyRef,
  itemIds: contentsItemIds,
  topOffset: () => props.topOffset,
  contentKey: explanationHtmlWithAnchors,
});

const sanitizedAnswerImageCaptions = computed(() =>
  sanitizeCaptionMap(props.answerImageCaptions),
);

const sanitizedUniversalImageCaptions = computed(() =>
  sanitizeCaptionMap(props.universalImageCaptions),
);

// Compute column values for grid layout
const answerImageColsValue = computed(() => {
  if (props.answerImageCols === 2) return 6;
  if (props.answerImageCols === 3) return 4;
  return 12;
});

const universalImagesColsValue = computed(() => {
  if (props.universalImagesCols === 2) return 6;
  if (props.universalImagesCols === 3) return 4;
  return 12;
});
</script>

<style lang="scss" scoped>
.explanation-viewer {
  .answer-title {
    font-size: 1.75rem;
    font-weight: 600;
  }

  .explanation-viewer__title {
    margin: 1.5rem 0 1rem;
  }

  .explanation-viewer__reader {
    display: block;
  }

  .explanation-viewer__article {
    min-width: 0;
  }

  .explanation-viewer__body {
    overflow-x: auto;

    :deep(*) {
      color: rgb(var(--v-theme-on-surface));
    }

    :deep(p) {
      margin: 1em 0;
      line-height: 1.5;
      display: block;
      min-height: 1em;
    }

    :deep(p:empty) {
      height: 1em;
      margin: 1em 0;
      display: block;
    }

    :deep(span) {
      display: inline;
    }

    :deep(blockquote) {
      margin: 1em 0;
      padding: 0.5em 1em;
      border-left: 4px solid rgb(var(--v-theme-primary));
      background-color: rgba(var(--v-theme-primary), 0.05);
      font-style: italic;
    }

    :deep(blockquote p:first-child) {
      margin-top: 0;
    }

    :deep(blockquote p:last-child) {
      margin-bottom: 0;
    }

    :deep(table) {
      border-collapse: collapse;
      margin: 1rem 0;
      width: 100%;
      border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
    }

    :deep(td),
    :deep(th) {
      border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
      padding: 0.5rem;
      position: relative;
      text-align: left;
      overflow-wrap: break-word;
      word-wrap: break-word;
    }

    :deep(th) {
      background-color: rgba(var(--v-theme-on-surface), 0.04);
      font-weight: bold;
    }

    :deep(ul),
    :deep(ol) {
      margin: 0.5em 0;
      padding-left: 2em;
      list-style-position: outside;
    }

    :deep(li) {
      margin: 0.5em 0;
      padding-left: 0.5em;
      line-height: 1.4;
    }

    :deep(h1),
    :deep(h2),
    :deep(h3),
    :deep(h4),
    :deep(h5),
    :deep(h6) {
      margin-top: 1.5em;
      margin-bottom: 0.5em;
      line-height: 1.2;
      font-weight: 600;
    }

    :deep(h1[id]),
    :deep(h2[id]),
    :deep(h3[id]) {
      scroll-margin-top: calc(var(--ec-offset, 72px) + 1rem);
    }
  }
}
</style>
